import { Link } from "react-router-dom";
import { Zap, ArrowLeft } from "lucide-react";

export default function TermsOfService() {
  const effectiveDate = "February 2026";
  const contactEmail = "support@postqik.com";
  const legalEntity = "VZV Ltd";

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="tos-page">
      <header className="border-b border-zinc-200">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2" data-testid="tos-home-link">
            <span className="rounded-md bg-gradient-to-br from-fuchsia-600 to-cyan-600 p-1.5 text-white">
              <Zap className="w-4 h-4" />
            </span>
            <span className="font-display font-black tracking-tight text-slate-900">PostQik</span>
          </Link>
          <Link
            to="/login"
            className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900"
            data-testid="tos-back-link"
          >
            <ArrowLeft className="w-4 h-4" /> Back to sign in
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 sm:px-8 py-10 sm:py-14">
        <div className="text-[10px] sm:text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">
          Legal
        </div>
        <h1 className="mt-1 font-display font-black tracking-tight text-3xl sm:text-4xl lg:text-5xl text-slate-900">
          Terms of Service
        </h1>
        <p className="mt-2 text-sm text-slate-500">Effective date: {effectiveDate}</p>

        <div className="legal-content mt-8" data-testid="tos-body">
          <p>
            These Terms of Service (&ldquo;Terms&rdquo;) govern your use of PostQik, a service operated by {legalEntity}
            (&ldquo;PostQik&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;), including the PostQik web application and related services
            (the &ldquo;Service&rdquo;). By creating an account or using the Service you agree to these Terms. If you don&apos;t agree,
            don&apos;t use the Service.
          </p>

          <h2>1. Who We Are</h2>
          <p>PostQik is a tool that lets you send videos and photo carousels from your Google Drive to TikTok and/or Instagram as drafts, via our publishing partner Upload-Post. It is a workflow bridge — we do not host, moderate, or take ownership of your content.</p>

          <h2>2. Account Eligibility</h2>
          <ul>
            <li>You must be at least 13 years old (16 in the EU/UK) to use the Service.</li>
            <li>You must provide accurate registration information and keep it up to date.</li>
            <li>Accounts require admin approval. We may decline or revoke access at our discretion.</li>
            <li>You are responsible for keeping your password confidential and for all activity under your account.</li>
          </ul>

          <h2>3. Third-Party Accounts</h2>
          <p>To use PostQik you must connect at least one third-party account (Google Drive, and TikTok and/or Instagram via Upload-Post). You are solely responsible for:</p>
          <ul>
            <li>Complying with the terms of service and community guidelines of each connected platform.</li>
            <li>Ensuring the OAuth permissions you grant remain valid.</li>
            <li>The content, captions, hashtags, and metadata you choose to publish.</li>
          </ul>

          <h2>4. Your Content</h2>
          <p>You retain full ownership of the files and captions you send through PostQik (&ldquo;Your Content&rdquo;). By using the Service you grant us a limited, non-exclusive, worldwide license to transmit and process Your Content strictly for the purpose of sending it to the destination platform you selected. This license ends the moment the send completes.</p>
          <p>You represent and warrant that:</p>
          <ul>
            <li>You own Your Content or have all necessary rights, licenses, and permissions to publish it.</li>
            <li>Your Content does not infringe any third party&apos;s intellectual property, privacy, or other rights.</li>
            <li>Your Content complies with all applicable laws and with the terms of the destination platforms.</li>
          </ul>

          <h2>5. Acceptable Use</h2>
          <p>You agree not to use the Service to:</p>
          <ul>
            <li>Publish content that is illegal, defamatory, harassing, sexually explicit involving minors, or that incites violence.</li>
            <li>Impersonate any person or entity, or misrepresent your affiliation with anyone.</li>
            <li>Distribute malware, spam, phishing content, or engage in coordinated inauthentic behaviour.</li>
            <li>Attempt to reverse engineer, decompile, or bypass rate limits or quotas.</li>
            <li>Use the Service to scrape or bulk-extract Google Drive data for purposes unrelated to your own publishing.</li>
            <li>Resell, sublicense, or provide the Service to third parties without our written consent.</li>
          </ul>
          <p>We may suspend or terminate accounts that violate these rules, with or without notice.</p>

          <h2>6. Plans, Quotas &amp; Billing</h2>
          <ul>
            <li>Paid plans (Basic, Pro, Ultra) are billed monthly or annually via Stripe.</li>
            <li>Each plan includes a daily quota of video and carousel posts. Quotas reset at 00:00 UTC.</li>
            <li>Subscriptions renew automatically until cancelled. You can cancel any time from your billing settings.</li>
            <li>Except where required by law, subscription fees are non-refundable. Cancelling stops the next renewal but does not refund the current period.</li>
            <li>We may change plans, quotas, or pricing with at least 14 days&apos; notice. Existing subscribers keep their existing terms until their next renewal.</li>
            <li>Comp / free plans granted by an admin can be revoked at any time.</li>
            <li>Discount codes are subject to their individual terms (validity period, single use, etc.).</li>
          </ul>

          <h2>7. Free Trial</h2>
          <p>New accounts may receive a limited free trial period. Trial usage is non-transferable and subject to daily quotas. When the trial ends you must subscribe to continue publishing.</p>

          <h2>8. Third-Party Services</h2>
          <p>The Service depends on Google APIs, Upload-Post, Stripe, TikTok, and Instagram. We are not responsible for outages, policy changes, account suspensions, content removals, or data losses caused by any third-party service. You use those services under their respective terms.</p>

          <h2>9. Availability &amp; Changes to the Service</h2>
          <p>We aim to keep the Service available but do not guarantee uptime. We may add, change, or remove features at any time. If a change materially reduces the Service you paid for, you may cancel and receive a pro-rated refund for the unused portion of the current period.</p>

          <h2>10. Intellectual Property</h2>
          <p>The Service (excluding Your Content and third-party materials) — including its code, design, logos, and text — is owned by PostQik and protected by intellectual property laws. We grant you a limited, non-exclusive, non-transferable licence to use the Service under these Terms. All other rights are reserved.</p>

          <h2>11. Termination</h2>
          <p>You can terminate your account at any time by contacting us or using in-app deletion (once available). We may suspend or terminate your account immediately if you violate these Terms, if required by law, or if we discontinue the Service. On termination we delete your data as described in the Privacy Policy.</p>

          <h2>12. Disclaimer of Warranties</h2>
          <p>THE SERVICE IS PROVIDED &ldquo;AS IS&rdquo; AND &ldquo;AS AVAILABLE&rdquo; WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT, OR THAT THE SERVICE WILL BE UNINTERRUPTED OR ERROR-FREE. WE DO NOT GUARANTEE THAT ANY POST WILL SUCCEED, GO LIVE, OR REMAIN PUBLISHED.</p>

          <h2>13. Limitation of Liability</h2>
          <p>TO THE MAXIMUM EXTENT PERMITTED BY LAW, POSTQIK, ITS OFFICERS, EMPLOYEES, AND AGENTS WILL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, REVENUES, DATA, OR GOODWILL, ARISING OUT OF OR IN CONNECTION WITH THE SERVICE. OUR AGGREGATE LIABILITY FOR ANY CLAIM RELATING TO THE SERVICE WILL NOT EXCEED THE GREATER OF (A) THE AMOUNT YOU PAID US IN THE 12 MONTHS BEFORE THE EVENT GIVING RISE TO THE CLAIM, OR (B) USD 50.</p>

          <h2>14. Indemnification</h2>
          <p>You agree to indemnify and hold harmless PostQik from any claim, damage, loss, or expense (including reasonable legal fees) arising from Your Content, your violation of these Terms, or your violation of any law or third-party right.</p>

          <h2>15. Governing Law &amp; Disputes</h2>
          <p>These Terms are governed by the laws applicable at the operator&apos;s registered address, without regard to conflict-of-laws principles. Any dispute will be resolved in the competent courts of that jurisdiction, unless mandatory consumer-protection law grants you a different forum.</p>

          <h2>16. Changes to These Terms</h2>
          <p>We may update these Terms from time to time. Material changes will be announced at least 14 days before they take effect via email or in-app notice. Continued use of the Service after the effective date constitutes acceptance.</p>

          <h2>17. Contact</h2>
          <p>Questions or notices under these Terms: <a href={`mailto:${contactEmail}`}>{contactEmail}</a>.</p>
        </div>

        <div className="mt-14 pt-8 border-t border-zinc-200 flex items-center justify-between text-xs text-slate-500">
          <Link to="/privacy" className="hover:text-slate-900" data-testid="tos-to-privacy-link">
            Privacy Policy
          </Link>
          <Link to="/login" className="hover:text-slate-900">
            Sign in
          </Link>
        </div>
      </main>
    </div>
  );
}
